import { useState } from "react";
import { ArrowLeft, FileDown, Loader2, CheckCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useCreditPrompt } from "@/hooks/useCreditPrompt";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const Section = ({ title, children }) => (
  <div className="border-b-2 border-[#E4E4E7] pb-4 mb-4">
    <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
    {children}
  </div>
);

const Field = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-[#71717A] font-medium">{label}</span>
      <span className="font-semibold text-[#0A0A0A] text-right max-w-[60%]">{String(value)}</span>
    </div>
  );
};

const INSPECTION_LABELS = {
  "1.0": "Condition of consumer's intake equipment",
  "2.0": "Parallel or switched alternative sources of supply",
  "3.0": "Protective measure: ADS",
  "4.0": "Basic protection",
  "5.0": "Protective measures other than ADS",
  "6.0": "Additional protection",
  "7.0": "Distribution equipment",
  "8.0": "Circuits (Distribution and Final)",
  "9.0": "Isolation and switching",
  "10.0": "Current-using equipment",
  "11.0": "Identification and notices",
  "12.0": "Location(s) containing bath or shower",
  "13.0": "Other special installations or locations",
  "14.0": "Prosumer's low voltage installation(s)",
};

export default function DomesticEICStep5({ formData, onPrev, user, editCertId }) {
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const { showModal, modalCreditType, promptBuyCredits, closeModal, buyCredits } = useCreditPrompt();

  const buildPayload = () => ({
    ...formData,
    cert_date: formData.cert_date ? format(formData.cert_date, "dd/MM/yyyy") : "",
  });

  const saveContractorDetails = async () => {
    try {
      await authFetch(`${API}/api/user/contractor-details`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          contractor_trading_title: formData.contractor_trading_title,
          contractor_email: formData.contractor_email,
          contractor_phone: formData.contractor_phone,
          contractor_web: formData.contractor_web,
          contractor_reg_no: formData.contractor_reg_no,
          contractor_branch_no: formData.contractor_branch_no,
          contractor_address: formData.contractor_address,
          contractor_postcode: formData.contractor_postcode,
        }),
      });
    } catch { /* ignore */ }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await authFetch(`${API}/api/certificates/domestic-eic/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    setSubmitting(true);
    try {
      const isEdit = !!editCertId;
      const url = isEdit ? `${API}/api/certificates/${editCertId}` : `${API}/api/certificates/domestic-eic`;
      const method = isEdit ? "PUT" : "POST";
      const res = await authFetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (res.status === 402) { promptBuyCredits("electrical"); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate certificate");
      }
      const data = await res.json();
      const certId = data.certificate_id || editCertId;
      setPdfUrl(`${API}/api/certificates/domestic-eic/${certId}/download`);
      setEmailSent(data.email_sent || false);
      setCompleted(true);
      toast.success(isEdit ? "Certificate updated!" : "Certificate generated successfully!");
      await saveContractorDetails();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return (
      <div className="form-container max-w-3xl mx-auto text-center" data-testid="eic-step-5-complete">
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Certificate Generated
        </h2>
        <p className="text-sm text-[#71717A] mb-6">
          Your Domestic Electrical Installation Certificate has been created.
          {emailSent && " An email copy has been sent to the client and contractor."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="eic-download-pdf"
              className="brutalist-btn flex items-center justify-center gap-2">
              <FileDown className="w-4 h-4" /> Download PDF
            </a>
          )}
          <a href="/dashboard" data-testid="eic-back-dashboard" className="brutalist-btn-outline flex items-center justify-center gap-2">
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="eic-step-5">
      <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Review & Generate</h2>
      <p className="text-xs text-[#71717A] mb-6">Review all details below before generating the certificate PDF.</p>

      {/* Page 1 Summary */}
      <Section title="Client & Installation">
        <Field label="Client" value={formData.client_name} />
        <Field label="Client email" value={formData.client_email} />
        <Field label="Description" value={formData.installation_description} />
        <Field label="Extent" value={formData.installation_extent} />
        <Field label="Address" value={formData.installation_address} />
        <Field label="Postcode" value={formData.installation_postcode} />
        <Field label="Installation type" value={formData.installation_type} />
        <Field label="Departures from BS 7671" value={formData.departures} />
        <Field label="Designer / Inspector" value={formData.designer_name} />
        <Field label="Constructor" value={formData.constructor_name} />
        <Field label="Date" value={formData.cert_date ? format(formData.cert_date, "dd/MM/yyyy") : ""} />
      </Section>

      <Section title="Contractor">
        <Field label="Trading title" value={formData.contractor_trading_title} />
        <Field label="Email" value={formData.contractor_email} />
        <Field label="Phone" value={formData.contractor_phone} />
        <Field label="Address" value={formData.contractor_address} />
        <Field label="Postcode" value={formData.contractor_postcode} />
        <Field label="Reg No." value={formData.contractor_reg_no} />
        <Field label="Next inspection" value={formData.next_inspection_interval} />
        <Field label="Comments" value={formData.comments} />
      </Section>

      {/* Page 2 Summary */}
      <Section title="Supply & Earthing">
        <Field label="Earthing system" value={formData.supply_system} />
        <Field label="Live conductors" value={formData.supply_conductors} />
        <Field label="Voltage" value={formData.nominal_voltage ? `${formData.nominal_voltage} V` : ""} />
        <Field label="Frequency" value={formData.nominal_frequency ? `${formData.nominal_frequency} Hz` : ""} />
        <Field label="External Ze" value={formData.external_ze ? `${formData.external_ze} Ω` : ""} />
        <Field label="PFC" value={formData.pfc ? `${formData.pfc} kA` : ""} />
        <Field label="Primary supply BS" value={formData.primary_supply_bs} />
        <Field label="Rated current" value={formData.primary_supply_rated_current ? `${formData.primary_supply_rated_current} A` : ""} />
        <Field label="Means of earthing" value={formData.earthing_means} />
        <Field label="Measured Ze" value={formData.measured_ze ? `${formData.measured_ze} Ω` : ""} />
        <Field label="Main switch type" value={formData.main_switch_type} />
        <Field label="Main switch current" value={formData.main_switch_rated_current ? `${formData.main_switch_rated_current} A` : ""} />
        <Field label="Max demand" value={formData.max_demand ? `${formData.max_demand} A` : ""} />
      </Section>

      {/* Page 3 Summary - Inspections */}
      <Section title="Schedule of Inspections">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          {Object.entries(formData.inspections || {}).map(([id, outcome]) => (
            <div key={id} className="flex justify-between py-1 text-sm border-b border-[#F3F4F6]">
              <span className="text-[#71717A]">
                <span className="font-bold text-[#00A3E0]">{id}</span> {INSPECTION_LABELS[id] || ""}
              </span>
              <span className={`font-bold ${
                outcome === "Pass" ? "text-emerald-600" :
                outcome === "C1" ? "text-red-500" :
                outcome === "C2" ? "text-orange-400" :
                outcome === "C3" ? "text-yellow-500" : "text-[#71717A]"
              }`}>{outcome === "Pass" ? "\u2713" : outcome}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Page 4 Summary - Circuits */}
      <Section title="Distribution Board & Circuits">
        <Field label="DB ref" value={formData.db_ref} />
        <Field label="DB location" value={formData.db_location} />
        <Field label="Zs at board" value={formData.db_ze ? `${formData.db_ze} Ω` : ""} />
        <Field label="DB rating" value={formData.db_rating ? `${formData.db_rating} A` : ""} />
        <div className="mt-3">
          <p className="text-xs font-bold uppercase text-[#71717A] mb-2">Circuits ({(formData.circuits || []).length})</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-[#0A0A0A] text-white">
                  <th className="p-1.5 text-left">Ref</th>
                  <th className="p-1.5 text-left">Designation</th>
                  <th className="p-1.5">OCPD</th>
                  <th className="p-1.5">Rating</th>
                  <th className="p-1.5">R1+R2</th>
                  <th className="p-1.5">Zs</th>
                  <th className="p-1.5">IR L-E</th>
                  <th className="p-1.5">Polarity</th>
                  <th className="p-1.5">RCD</th>
                </tr>
              </thead>
              <tbody>
                {(formData.circuits || []).map((c, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}>
                    <td className="p-1.5 font-bold">{c.ref}</td>
                    <td className="p-1.5">{c.designation}</td>
                    <td className="p-1.5 text-center">{c.ocpd_type}</td>
                    <td className="p-1.5 text-center">{c.ocpd_rating}</td>
                    <td className="p-1.5 text-center">{c.r1r2}</td>
                    <td className="p-1.5 text-center">{c.zs}</td>
                    <td className="p-1.5 text-center">{c.ir_live_earth}</td>
                    <td className="p-1.5 text-center">{c.polarity}</td>
                    <td className="p-1.5 text-center">{c.rcd_time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
        <button data-testid="eic-step-5-prev" onClick={onPrev} className="brutalist-btn-outline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        <div className="flex gap-3">
          <button data-testid="eic-preview-btn" onClick={handlePreview} disabled={previewing}
            className="brutalist-btn-outline flex items-center gap-2">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Preview PDF
          </button>
          <button data-testid="eic-generate-btn" onClick={handleGenerate} disabled={submitting}
            className="brutalist-btn flex items-center gap-2">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileDown className="w-4 h-4" /> Generate Certificate</>}
          </button>
        </div>
      </div>
      <BuyCreditsModal open={showModal} onClose={closeModal} creditType={modalCreditType} onBuy={buyCredits} />
    </div>
  );
}
